/**
 * 올린 HTML 이 바깥에서 불러오는 곳을 알린다(2026-11-03) — 서버가 받을 때 훑어 둔 출처(`html_external`,
 * `@signageone/core` 의 `html-external.ts`).
 *
 * 화면은 HTML 을 샌드박스에 띄우고 https 만 연다. 바깥 주소에서 불러오는 부분은 인터넷이 있어야 나오고(폐쇄망 현장에서는
 * 조용히 빈다), 그 주소의 파일이 바뀌면 콘솔에서 승인한 뒤에도 화면이 바뀐다. 고객사가 바깥 연결을 막아 두었으면
 * (`offline`) 화면은 그 부분을 아예 부르지 않는다. http 주소는 어느 쪽이든 화면에서 열리지 않는다.
 */
import type { ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { insecureOrigin } from '@signageone/core';
import { useBrand } from './brand.js';

export interface ExternalNotice {
  title: string;
  /** 불러오는 곳(https·wss) — 사람이 읽게 이름만. */
  hosts: string[];
  /** 화면에서 열리지 않는 곳(http·ws). */
  blocked: string[];
}

const hostOf = (origin: string): string => origin.replace(/^[a-z]+:\/\//i, '');

/** 알릴 것 — 바깥에서 불러오지 않거나 아직 모르면 null. */
export function externalNotice(origins: readonly string[] | null | undefined, offline: boolean): ExternalNotice | null {
  if (!origins || origins.length === 0) return null;
  const hosts = origins.filter((origin) => !insecureOrigin(origin)).map(hostOf);
  const blocked = origins.filter(insecureOrigin).map(hostOf);
  const title =
    hosts.length === 0
      ? `http 주소 ${blocked.length}곳에서 불러오려 하지만 화면에서는 열리지 않습니다 — https 주소로 바꾸세요`
      : offline
        ? `바깥 주소 ${hosts.length}곳에서 불러오지만, 바깥 연결을 막아 두어 화면에서는 그 부분이 빠집니다`
        : `바깥 주소 ${hosts.length}곳에서 불러옵니다 — 인터넷이 끊기면 그 부분이 빠지고, 그 주소의 파일이 바뀌면 화면도 바뀝니다`;
  return { title, hosts, blocked: hosts.length === 0 ? [] : blocked };
}

/** 한 줄 요약 — 고르기 칸·올린 뒤 안내처럼 좁은 곳에. */
export function externalSummary(origins: readonly string[] | null | undefined, offline: boolean): string | null {
  const notice = externalNotice(origins, offline);
  if (!notice) return null;
  if (notice.hosts.length === 0) return `http 주소 ${notice.blocked.length}곳은 화면에서 열리지 않습니다`;
  return offline
    ? `바깥 주소 ${notice.hosts.length}곳 — 바깥 연결을 막아 두어 그 부분은 빠집니다`
    : `바깥 주소 ${notice.hosts.length}곳에서 불러옵니다 — 인터넷이 있어야 나오는 부분이 있습니다`;
}

export function HtmlExternalNote({ origins }: { origins: readonly string[] | null | undefined }): ReactNode {
  // 고객사가 바깥 연결을 막았는지는 환경 설정에서(`brand.tsx` 가 함께 읽는다).
  const { htmlOffline } = useBrand();
  const notice = externalNotice(origins, htmlOffline);
  if (!notice) return null;
  return (
    <div className="html-external" role="note">
      <AlertTriangle className="html-external__icon h-4 w-4" strokeWidth={2} aria-hidden="true" />
      <div className="min-w-0">
        <p className="html-external__title">{notice.title}</p>
        {notice.hosts.length > 0 ? <p className="html-external__hosts">{notice.hosts.join(' · ')}</p> : null}
        {notice.blocked.length > 0 ? (
          <p className="html-external__hosts">http 라 화면에서 열리지 않는 곳: {notice.blocked.join(' · ')}</p>
        ) : null}
      </div>
    </div>
  );
}
